import React, { useState, useEffect } from 'react'
import { useParams, useLocation } from 'react-router-dom'
import EventSearchSection from '../components/EventForm/EventSearchSection'
import BasicEventInfo from '../components/EventForm/BasicEventInfo'
import AttendanceSection from '../components/EventForm/AttendanceSection'
import EventReviewSection from '../components/EventForm/EventReviewSection'
import ArtistSection from '../components/EventForm/ArtistSection'
import MediaUpload from '../components/MediaUpload'
import LoadingSpinner from '../components/UI/LoadingSpinner'
import CollapsibleSection from '../components/UI/CollapsibleSection'
import { useNewEvent } from '../hooks/useNewEvent'
import { useEventSearch } from '../hooks/useEventSearch'

const NewEvent = ({ mode = 'create' }) => {
  useParams() // Route params available if needed
  const location = useLocation()
  const [sectionsOpen, setSectionsOpen] = useState({
    basic: false,
    attendance: false,
    artists: false,
    review: false,
    media: false
  })

  const {
    event,
    setEvent,
    loading,
    error,
    success,
    isEditMode,
    eventId,
    handleInputChange,
    handleAttendedDatesChange,
    addAttendedDate,
    removeAttendedDate,
    handleArtistChange,
    addArtist,
    removeArtist,
    submitEvent
  } = useNewEvent(mode)

  // Open all sections if there's a pre-selected event from discover widget
  useEffect(() => {
    if (location.state?.selectedEvent) {
      setSectionsOpen({
        basic: true,
        attendance: true,
        artists: true,
        review: true,
        media: true
      })
    }
  }, [location.state?.selectedEvent])

  const eventSearchProps = useEventSearch((selectedEvent) => {
    setEvent(prev => ({ ...prev, ...selectedEvent }))
    // Open all sections when an event is selected
    setSectionsOpen({
      basic: true,
      attendance: true,
      artists: true,
      review: true,
      media: true
    })
  })

  const handleSubmit = async (e, skipDuplicateCheck = false) => {
    e.preventDefault()
    await submitEvent([], skipDuplicateCheck) // Media is uploaded separately via MediaUpload component
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900 flex items-center justify-center">
        <LoadingSpinner size="large" />
      </div>
    )
  }

  return (
    <div className="p-8">
      <div className="max-w-4xl mx-auto relative z-10">
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold mb-2" style={{color: 'var(--theme-text-primary)'}}>
            {isEditMode ? '✏️ Edit Event' : 'Add a New Event'}
          </h1>
          <p style={{color: 'var(--theme-text-secondary)'}}>
            {isEditMode ? 'Update your event details' : 'Add a new event to your music journey'}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-8">

          {!isEditMode && (
            <EventSearchSection {...eventSearchProps} />
          )}

          <CollapsibleSection
            title="Basic Event Information"
            icon="📝"
            isOpen={sectionsOpen.basic}
            onToggle={() => setSectionsOpen(prev => ({ ...prev, basic: !prev.basic }))}
          >
            <BasicEventInfo event={event} onChange={handleInputChange} />
          </CollapsibleSection>

          <CollapsibleSection
            title="Attendance Information"
            icon="🎟️"
            isOpen={sectionsOpen.attendance}
            onToggle={() => setSectionsOpen(prev => ({ ...prev, attendance: !prev.attendance }))}
          >
            <AttendanceSection
              event={event}
              onChange={handleInputChange}
              onAttendedDatesChange={handleAttendedDatesChange}
              onRemoveAttendedDate={removeAttendedDate}
              onAddAttendedDate={addAttendedDate}
            />
          </CollapsibleSection>

          <CollapsibleSection
            title="Artists & Lineup"
            icon="🎤"
            isOpen={sectionsOpen.artists}
            onToggle={() => setSectionsOpen(prev => ({ ...prev, artists: !prev.artists }))}
          >
            <ArtistSection
              event={event}
              onArtistChange={handleArtistChange}
              onAddArtist={addArtist}
              onRemoveArtist={removeArtist}
            />
          </CollapsibleSection>

          <CollapsibleSection
            title="Review & Rating"
            icon="⭐"
            isOpen={sectionsOpen.review}
            onToggle={() => setSectionsOpen(prev => ({ ...prev, review: !prev.review }))}
          >
            <EventReviewSection event={event} onChange={handleInputChange} />
          </CollapsibleSection>

          <CollapsibleSection
            title="Media Upload"
            icon="📸"
            isOpen={sectionsOpen.media}
            onToggle={() => setSectionsOpen(prev => ({ ...prev, media: !prev.media }))}
          >
            <MediaUpload
              eventId={eventId}
              onUploadSuccess={(data) => {
                // Optionally refresh event data after upload
              }}
              onError={(errorMsg) => {
                if (process.env.NODE_ENV === 'development') console.error('Upload error:', errorMsg)
              }}
            />
          </CollapsibleSection>

          {error && (
            <div>
              {typeof error === 'object' && error.type === 'duplicate' ? (
                <div className="bg-yellow-500/20 border border-yellow-500 px-6 py-4 rounded-xl space-y-4" style={{color: 'var(--theme-text-primary)'}}>
                  <div className="font-semibold">⚠️ {error.message}</div>
                  {error.duplicates && error.duplicates.length > 0 && (
                    <div className="space-y-2">
                      <p className="text-sm opacity-80">You already have similar events in your collection:</p>
                      <ul className="space-y-2">
                        {error.duplicates.map((dup, idx) => (
                          <li key={idx} className="text-sm bg-white/5 p-2 rounded">
                            <div className="font-medium">{dup.name}</div>
                            <div className="text-xs opacity-70">{dup.venueName} • {new Date(dup.startDate).toLocaleDateString()}</div>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {error.canProceed && (
                    <button
                      type="button"
                      onClick={(e) => handleSubmit(e, true)}
                      className="bg-yellow-600 hover:bg-yellow-700 text-white font-medium py-2 px-4 rounded-lg transition-all duration-200"
                    >
                      Add Anyway
                    </button>
                  )}
                </div>
              ) : (
                <div className="bg-red-500/20 border border-red-500 px-6 py-4 rounded-xl" style={{color: 'var(--theme-text-primary)'}}>
                  {typeof error === 'string' ? error : error.message}
                </div>
              )}
            </div>
          )}

          {success && (
            <div className="bg-green-500/20 border border-green-500 px-6 py-4 rounded-xl" style={{color: 'var(--theme-text-primary)'}}>
              {success}
            </div>
          )}

          <div className="flex gap-4">
            <button
              type="submit"
              disabled={loading}
              className="flex-1 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white font-semibold py-3 px-6 rounded-xl transition-all duration-300 disabled:opacity-50"
            >
              {loading ? 'Saving...' : (isEditMode ? 'Update Event' : 'Create Event')}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default NewEvent
